import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { IconGrip } from './icons';

/**
 * ReorderableList: a short ordered list the user can rearrange.
 *
 * Reordering happens in two equivalent ways, both reversible before save:
 * - pointer: drag the grip; the list reorders live as the pointer passes
 *   other rows (no drop indicator to aim at);
 * - keyboard: focus the grip and press ↑ / ↓ (Home / End for the ends).
 *   Each move is announced through a polite live region.
 *
 * The list knows nothing about its items: `renderItem` draws the row body,
 * `onMove(from, to)` reports the new order. Rows are hairline-separated, not
 * boxed. Not for long lists (>15) — order those with a sort key instead.
 */
export function ReorderableList<T>({
  items,
  keyOf,
  onMove,
  renderItem,
  itemLabel,
  numbered,
  numberFrom = 1,
  disabled,
  className,
  'aria-label': ariaLabel,
}: {
  items: T[];
  keyOf: (item: T, index: number) => string | number;
  onMove: (from: number, to: number) => void;
  renderItem: (item: T, index: number) => React.ReactNode;
  /** Short name for announcements, e.g. (item) => `question 2`. */
  itemLabel: (item: T, index: number) => string;
  numbered?: boolean;
  /** First visible number when a pinned row sits above this list. */
  numberFrom?: number;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
}) {
  const [dragging, setDragging] = useState<number | null>(null);
  const [gripHeld, setGripHeld] = useState<number | null>(null);
  const [announce, setAnnounce] = useState('');
  const gripRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // A row is draggable only while its grip is held, so a drag can't start
  // from the row body and text selection inside a row keeps working. The
  // guard has to be the `draggable` attribute rather than a check inside
  // `dragstart`: the event fires at the element carrying `draggable`, not at
  // the grip the pointer went down on, so it cannot tell them apart.
  useEffect(() => {
    if (gripHeld === null) return;
    const release = () => setGripHeld(null);
    // A plain click ends in pointerup; a real drag suppresses it and ends in
    // dragend instead.
    document.addEventListener('pointerup', release);
    document.addEventListener('pointercancel', release);
    document.addEventListener('dragend', release);
    return () => {
      document.removeEventListener('pointerup', release);
      document.removeEventListener('pointercancel', release);
      document.removeEventListener('dragend', release);
    };
  }, [gripHeld]);

  const move = (from: number, to: number, focus = false) => {
    if (to < 0 || to >= items.length || from === to) return;
    onMove(from, to);
    const last = items.length + numberFrom - 1;
    setAnnounce(
      `${itemLabel(items[from], from)} moved to position ${to + numberFrom} of ${last}.`,
    );
    if (focus) requestAnimationFrame(() => gripRefs.current[to]?.focus());
  };

  const onKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (disabled) return;
    const to: Record<string, number> = {
      ArrowUp: index - 1,
      ArrowDown: index + 1,
      Home: 0,
      End: items.length - 1,
    };
    if (!(e.key in to)) return;
    e.preventDefault();
    move(index, to[e.key], true);
  };

  return (
    <div className={className}>
      <ol role="list" aria-label={ariaLabel} className="flex flex-col">
        {items.map((item, index) => (
          <li
            key={keyOf(item, index)}
            draggable={!disabled && gripHeld === index}
            onDragStart={(e) => {
              // Firefox refuses to start a drag that carries no data.
              e.dataTransfer.setData('text/plain', itemLabel(item, index));
              e.dataTransfer.effectAllowed = 'move';
              setDragging(index);
            }}
            onDragOver={(e) => {
              if (dragging === null || dragging === index) return;
              e.preventDefault();
              move(dragging, index);
              setDragging(index);
            }}
            onDrop={(e) => e.preventDefault()}
            onDragEnd={() => {
              setDragging(null);
              setGripHeld(null);
            }}
            className={clsx(
              'flex items-start gap-2 border-b border-v2-line/70 py-2 last:border-b-0',
              dragging === index && 'bg-v2-sunken/80 opacity-70',
            )}
          >
            <button
              ref={(el) => {
                gripRefs.current[index] = el;
              }}
              type="button"
              data-grip
              disabled={disabled}
              aria-label={`Reorder ${itemLabel(item, index)}. Arrow keys move it.`}
              onPointerDown={() => setGripHeld(index)}
              onKeyDown={(e) => onKeyDown(e, index)}
              className={clsx(
                'mt-0.5 inline-flex h-7 w-6 shrink-0 items-center justify-center rounded-v2 text-v2-fg-4 transition-colors',
                disabled
                  ? 'cursor-not-allowed opacity-50'
                  : 'cursor-grab hover:bg-v2-sunken hover:text-v2-fg-2 active:cursor-grabbing',
              )}
            >
              <IconGrip size={14} />
            </button>
            {numbered && (
              <span
                aria-hidden
                className="v2-num mt-1.5 w-4 shrink-0 text-right text-v2-12 text-v2-fg-3"
              >
                {index + numberFrom}
              </span>
            )}
            <div className="min-w-0 flex-1">{renderItem(item, index)}</div>
          </li>
        ))}
      </ol>
      <p role="status" aria-live="polite" className="sr-only">
        {announce}
      </p>
    </div>
  );
}
